import { AxiosResponse } from "axios";
import { ICase } from "dto/Cases/ICase";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { axiosInstance } from "./axios";
import { IPackageListResponse } from "dto/IPackage";
import { IKey } from "dto/IKey";
import { IError } from "dto/IError";
import { IEntity } from "dto/IEntity";
import { IGetCasesResponse } from "dto/Cases/IGetCasesResponse";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { ICaseHistory } from "dto/Cases/ICaseHistory";
import { ItemKind } from "dto/IKindItems";
import { ICourtPortalCase } from "dto/Cases/ICourtPortalCase";
import { ICaseAccessGrant } from "dto/Cases/ICaseAccessGrant";

const queryKeys = {
  getSimpleCases: (payload: { text?: string }) => [
    "casesService.getSimpleCases",
    payload,
  ],
  searchCourtPortalCases: (payload: { signature?: string }) => [
    "casesService.searchCourtPortal",
    payload,
  ],
  searchSimilarCases: (payload: { title?: string }) => [
    "casesService.searchSimilarCases",
    payload,
  ],
  getCases: (payload: IGetCases) => ["caseService.getCases", payload],
  getFilter: (filterId: string) => ["caseService.getFilter", filterId],
  getCase: (id: string, searchText: string) => [
    "caseService.getCase",
    id,
    searchText,
  ],
  getCaseHistory: (id: string) => ["caseService.getCaseHistory", id],
  getCaseAccessGrants: (id: string) => ["caseService.getCaseAccessGrants", id],
  getDossier: (id: string) => ["caseService.getDossier", id],
  getKeys: (payload: ICaseGetKeys) => ["caseService.getKeys", payload],
  getAddressBook: (
    name?: string,
    eDeliveryAddress?: string,
    caseId?: string,
  ) => ["caseService.getAddressBook", name, eDeliveryAddress, caseId],
  searchCaseParticipantDuplicatesFromCourtPortal: (payload: {
    id?: number;
  }) => [
    "casesService.searchCaseParticipantDuplicatesFromCourtPortal",
    payload,
  ],
};

const getFiltersForCases = () => {
  return axiosInstance.get(`/case/filters`);
};

const useGetFile = () => {
  return useCustomMutation(getFiltersForCases);
};

const createFilterForCases = (payload: Record<string, unknown>) => {
  return axiosInstance.post("/case/filter", payload);
};

const useCreateFilterForCases = () => {
  return useCustomMutation(createFilterForCases);
};

const deleteFilter = (payload: { Id: string }) => {
  return axiosInstance.delete("/case/filter", { data: payload });
};

const useDeleteFilter = () => {
  return useCustomMutation(deleteFilter);
};

const getFilter = async (
  filterId: string,
  isEnabled: boolean,
): Promise<{ Name: string; List: number; Id: string }> => {
  if (!isEnabled) return null;

  const r = await axiosInstance.get(`/case/filter?id=${filterId}`);

  return r?.data;
};

const useGetFilter = (filterId: string, isEnabled: boolean) => {
  return useCustomQuery(queryKeys.getFilter(filterId), () =>
    getFilter(filterId, isEnabled),
  );
};

const getSimpleCases = async (payload: {
  text?: string;
  id?: string;
}): Promise<{ data: ICaseSimple[] }> => {
  const query = new URLSearchParams(window.location.search);

  if (payload.text?.length) {
    query.append("searchString", payload.text);
  }

  if (payload.id?.length) {
    query.append("id", payload.id);
  }

  return axiosInstance.get(`/cases/simple?${query}`);
};

const useGetSimpleCases = (
  payload: { text?: string; id?: string },
  enabled: boolean = true,
) => {
  return useCustomQuery(
    queryKeys.getSimpleCases(payload),
    () => getSimpleCases(payload),
    { enabled },
  );
};

const searchCourtPortalCases = async (payload: {
  signature?: string;
}): Promise<{ data: ICourtPortalCase[] }> => {
  const query = new URLSearchParams(window.location.search);

  if (payload.signature?.length) {
    query.append("signature", payload.signature);
  }

  // if (!!payload.id?.length) {
  //   query.append("id", payload.id);
  // }

  return axiosInstance.get(`/cases/search/courtPortal?${query}`);
};

const useSearchCourtPortalCases = (
  payload: { signature?: string },
  enabled,
) => {
  return useCustomQuery(
    queryKeys.searchCourtPortalCases(payload),
    () => searchCourtPortalCases(payload),
    { enabled: enabled },
  );
};

const searchSimilarCases = async (payload: {
  title?: string;
}): Promise<{ data: ICaseSimple[] }> => {
  const query = new URLSearchParams(window.location.search);

  if (payload.title?.length) {
    query.append("searchString", payload.title);
  }
  return axiosInstance.get(`/cases/similar?${query}`);
};

const useSearchSimilarCases = (payload: { title?: string }, enabled) => {
  return useCustomQuery(
    queryKeys.searchSimilarCases(payload),
    () => searchSimilarCases(payload),
    { enabled: enabled },
  );
};

const cloneCourtPortalCase = async (payload: {
  id?: number;
}): Promise<{ data: ICase }> => {
  const query = new URLSearchParams(window.location.search);

  if (payload.id) {
    query.append("id", payload.id.toString());
  }

  // if (!!payload.id?.length) {
  //   query.append("id", payload.id);
  // }

  return axiosInstance.get(`/cases/clone/courtPortal?${query}`);
};

const useCloneCourtPortalCase = () => {
  return useCustomMutation(cloneCourtPortalCase);
};

const searchCaseParticipantDuplicatesFromCourtPortal = async (payload: {
  id?: number;
}): Promise<{ data: ICaseParticipant[] }> => {
  const query = new URLSearchParams(window.location.search);

  if (payload.id) {
    query.append("id", payload.id.toString());
  }

  // if (!!payload.id?.length) {
  //   query.append("id", payload.id);
  // }

  return axiosInstance.get(`/client/similar?${query}`);
};

const useSearchCaseParticipantDuplicatesFromCourtPortal = (payload: {
  id?: number;
}) => {
  return useCustomQuery(
    queryKeys.searchCaseParticipantDuplicatesFromCourtPortal(payload),
    () => searchCaseParticipantDuplicatesFromCourtPortal(payload),
    { enabled: true },
  );
};

interface IGetCases {
  page: number;
  ParticipantId?: string | number;
  filterId?: string;
  Text?: string;
}

const getCases = (payload: IGetCases): Promise<{ data: IGetCasesResponse }> => {
  let url = `/cases?Page=${payload.page}&SortProp=UpdateDate&SortOrder=Descending`;

  if (payload.ParticipantId) {
    url += `&ParticipantId=${payload.ParticipantId}`;
  }
  if (payload.filterId) {
    url += `&FilterId=${payload.filterId}`;
  }
  if (payload.Text) {
    url += `&Text=${payload.Text}`;
  }

  return axiosInstance.get(url);
};

const useGetCases = (payload: IGetCases) => {
  return useCustomQuery(queryKeys.getCases(payload), () => getCases(payload));
};

const getCase = (
  id: string,
  enabled: boolean,
  searchText?: string,
): Promise<ICase> => {
  if (!id?.length || !enabled) {
    return {
      data: [],
    } as unknown as Promise<ICase>;
  }

  const query = new URLSearchParams();

  if (id) {
    query.append("id", id.toString());
  }

  if (searchText) {
    query.append("searchText", searchText.toString());
  }

  return axiosInstance.get(`/case?${query}`).then((r) => r.data);
};

const useGetCaseHistory = (id: string, enabled: boolean = true) => {
  return useCustomQuery(queryKeys.getCaseHistory(id), () =>
    getCaseHistory(id, enabled),
  );
};

const getCaseHistory = (
  id: string,
  enabled: boolean,
): Promise<ICaseHistory[]> => {
  if (!id?.length || !enabled) {
    return {
      data: [],
    } as unknown as Promise<ICaseHistory[]>;
  }

  return axiosInstance.get(`/case/history?id=${id}`).then((r) => r.data);
};

const useGetCase = (
  id: string,
  searchText: string = null,
  enabled: boolean = true,
) => {
  return useCustomQuery(queryKeys.getCase(id, searchText), () =>
    getCase(id, enabled, searchText),
  );
};

const getDossier = (
  id: string,
  folderId?: string | null,
): Promise<{ data: { Items: IDossierItem[]; Keys: IKey[]; ObjectKeys: IKey[] } }> => {
  let url = `/dossier?CaseId=${id}`;

  if (folderId) {
    url += `&folderId=${folderId}`;
  }
  return axiosInstance.get(url);
};

const useGetDossier = (
  id: string,
  folderId?: string | null,
  enabled: boolean = true,
) => {
  return useCustomQuery(
    queryKeys.getDossier(id),
    () => getDossier(id, folderId),
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
    },
  );
};

const getDossierMutation = ({
  id,
  folderId,
}: {
  id: string;
  folderId?: string | null;
}): Promise<{ data: IPackageListResponse }> => {
  let url = `/dossier?CaseId=${id}`;

  if (folderId) {
    url += `&folderId=${folderId}`;
  }

  return axiosInstance.get(url);
};

const useGetDossierMutation = () => {
  return useCustomMutation(getDossierMutation);
};

export interface ICaseGetKeys {
  id: string | null;
}

const getKeys = (
  payload: ICaseGetKeys,
): Promise<{ data: IKey[] }> | { data: IKey[] } => {
  if (!payload.id) {
    return { data: [] } as AxiosResponse;
  }

  return axiosInstance.get(`/case/keys?id=${payload.id}`);
};

const useGetKeys = (payload: ICaseGetKeys) => {
  return useCustomQuery(queryKeys.getKeys(payload), () => getKeys(payload));
};

export interface IAttachToCase {
  CaseId: string;
  ToAttachId: string;
  ToAttachKind: number;
  ObjectKey?: string | null;
  PublicKey?: string | null;
  ShowInDossier?: boolean;

  Dossier?: IAttachToCaseFolderLink[];
  TagIds?: string[];
}

export interface IAttachToCaseFolderLink {
  AttachmentId: string;
  FolderId?: string;
}

const attachToCase = (payload: IAttachToCase): Promise<{ data: IError }> => {
  return axiosInstance.post("/case/attach", payload);
};

const useAttachToCase = () => {
  return useCustomMutation(attachToCase);
};

export interface IAttachManyToCase {
  CaseId: string;
  ThreadId: string;
  Items: {
    Id: string;
    Kind: string;
    ObjectKey: string;
    PublicKey: string;
  }[];
  ShowInDossier?: boolean;
  Dossier?: IAttachToCaseFolderLink[];
  TagIds?: string[];
}

const attachManyToCase = (
  payload: IAttachManyToCase,
): Promise<{ data: IError }> => {
  return axiosInstance.post("case/attachMany", payload);
};

const useAttachManyToCase = () => {
  return useCustomMutation(attachManyToCase);
};

export interface ICompareParticipantsRequest {
  CaseId: string;
  ThreadId?: string;
  Items: {
    Id: string;
    Kind: ItemKind;
  }[];
}

export interface ICompareParticipantsResponse {
  CaseId: string;
  New: ICaseParticipant[];
  Existing: ICaseParticipant[];
}

const compareParticipants = (
  payload: ICompareParticipantsRequest,
): Promise<{ data: ICompareParticipantsResponse }> => {
  return axiosInstance.post("/case/attach/prepare", payload);
};

const useCompareParticipants = () => {
  return useCustomMutation(compareParticipants);
};

const getAddressBook = (
  name?: string,
  eDeliveryAddress?: string,
  caseId?: string,
  onlyUsers?: boolean,
): Promise<{ data: IEntity[] }> => {
  const query = new URLSearchParams(window.location.search);

  if (name?.length) {
    query.append("Name", name);
  } else {
    query.append("LastUsed", "true");
  }

  if (eDeliveryAddress?.length) {
    query.append("EDeliveryAddress", eDeliveryAddress);
  }

  if (caseId?.length) {
    query.append("caseId", caseId);
  }

  if (onlyUsers) {
    query.append("user", "true");
  }

  return axiosInstance.get(`/AddressBook/Search?${query}`);
};

const useGetAddressBook = (
  name?: string,
  eDeliveryAddress?: string,
  caseId?: string,
  onlyUsers?: boolean,
  enabled?: boolean,
) => {
  return useCustomQuery(
    queryKeys.getAddressBook(name, eDeliveryAddress, caseId),
    () => getAddressBook(name, eDeliveryAddress, caseId, onlyUsers),
    {
      enabled: enabled ?? true,
    },
  );
};

const searchEDeliveryAddress = (
  eDeliveryAddress?: string,
): Promise<{ data: IEntity[] }> => {
  return axiosInstance.get(
    `/AddressBook/Search?EDeliveryAddress=${eDeliveryAddress}`,
  );
};

const useSearchEDeliveryAddress = () => {
  return useCustomMutation(searchEDeliveryAddress);
};

export interface INewTag {
  Id: null;
  Name: string;
  Type: number;
}

export interface ICreateCase {
  Id?: string | null;
  NewTags?: INewTag[];
  Participants?: ICaseParticipant[];
  Signature?: string;
  TagIds?: string[];
  Title: string;
  StatusId?: number;
  Folders?: string[];
  /** Klucz szyfrujący sprawy (base64) — prywatny zaszyfrowany kluczem publicznym profilu. */
  PublicKey?: string;
  PrivateKey?: string;
}

const createCase = (payload: ICreateCase): Promise<{ data: ICase }> => {
  return axiosInstance.post("/case", payload);
};

const useCreateCase = () => {
  return useCustomMutation(createCase);
};

const deleteCase = (c: ICaseSimple): Promise<{ data: IError }> => {
  return axiosInstance.delete(`/case?id=${c.Id}`);
};

const useDeleteCase = () => {
  return useCustomMutation(deleteCase);
};

const archiveCase = (c: ICaseSimple): Promise<{ data: IError }> => {
  return axiosInstance.post(`/case/archive?id=${c.Id}`);
};

const useArchiveCase = () => {
  return useCustomMutation(archiveCase);
};

export interface IRedirect {
  CaseId: string;
  ReceiverId: string;
  Comment?: string;
  Keys: {
    Id: string;
    Key: string;
  }[];
}

const redirect = (payload: IRedirect): Promise<{ data: IError }> => {
  return axiosInstance.post("/case/pass", payload);
};

const useRedirect = () => {
  return useCustomMutation(redirect);
};

export interface IShareRequest {
  CaseId: string;

  Comment?: string;
  Keys: {
    Id: string;
    Key: string;
    ReceiverId: string;
  }[];
}

const share = (payload: IShareRequest) => {
  return axiosInstance.post("/case/share", payload);
};

const useShare = () => {
  return useCustomMutation(share);
};

// ============================================================================
// Model kluczy firmowych, F7 (Q4, "chiński mur"): IsRestricted + CaseAccessGrants.
// Zarządza wyłącznie autor sprawy — BE odrzuca każde inne wywołanie. Bariera
// wyłącznie serwerowa przy wspólnym kluczu firmy, nie warstwa szyfrowania.
// ============================================================================

const setCaseRestricted = (payload: {
  caseId: string;
  isRestricted: boolean;
}) => {
  return axiosInstance.post(`/case/${payload.caseId}/restricted`, {
    IsRestricted: payload.isRestricted,
  });
};

const useSetCaseRestricted = () => {
  return useCustomMutation(setCaseRestricted);
};

const getCaseAccessGrants = (
  caseId: string,
): Promise<{ data: ICaseAccessGrant[] }> => {
  return axiosInstance.get(`/case/${caseId}/accessGrants`);
};

const useGetCaseAccessGrants = (caseId: string, enabled: boolean = true) => {
  return useCustomQuery(
    queryKeys.getCaseAccessGrants(caseId),
    () => getCaseAccessGrants(caseId),
    { enabled: enabled && !!caseId },
  );
};

const addCaseAccessGrant = (payload: { caseId: string; userId: string }) => {
  return axiosInstance.post(`/case/${payload.caseId}/accessGrants`, {
    UserId: payload.userId,
  });
};

const useAddCaseAccessGrant = () => {
  return useCustomMutation(addCaseAccessGrant);
};

const removeCaseAccessGrant = (payload: { caseId: string; userId: string }) => {
  return axiosInstance.delete(
    `/case/${payload.caseId}/accessGrants/${payload.userId}`,
  );
};

const useRemoveCaseAccessGrant = () => {
  return useCustomMutation(removeCaseAccessGrant);
};

export default {
  useCreateFilterForCases,
  useGetFile,
  useGetSimpleCases,
  useGetCases,
  useGetCase,
  useGetDossier,
  useAttachToCase,
  useGetKeys,
  useGetAddressBook,
  useCreateCase,
  getKeys,
  useDeleteCase,
  useArchiveCase,
  useRedirect,
  useSearchEDeliveryAddress,
  useAttachManyToCase,
  useGetDossierMutation,
  useDeleteFilter,
  useGetFilter,
  useGetCaseHistory,
  useCompareParticipants,
  useSearchCourtPortalCases,
  useCloneCourtPortalCase,
  useSearchCaseParticipantDuplicatesFromCourtPortal,
  useSearchSimilarCases,
  useShare,
  useSetCaseRestricted,
  useGetCaseAccessGrants,
  useAddCaseAccessGrant,
  useRemoveCaseAccessGrant,
};
